import { createTheme } from '@mui/material/styles'

/**
 * TallyWise's palette.
 *
 * `#7ed348` is the brand green and the one fixed point here. Everything else is
 * derived from it or chosen to sit next to it.
 *
 * Two things it is NOT used for, deliberately:
 *
 *   1. Text on a white background. At 4.5:1 it fails WCAG AA by a wide margin —
 *      contrast against white is about 1.8:1. Where the brand colour has to carry
 *      words, they sit on `primary.dark` or on a dark surface.
 *   2. "Success". A palette where the brand colour and the success colour are the
 *      same leaves no way to say "this saved" that is distinguishable from
 *      "this is a TallyWise button". Success is a separate, bluer green.
 */
export const BRAND = '#7ed348'

/**
 * The brand green, dark enough to carry white type.
 *
 * ── Why a second green exists ───────────────────────────────────────────────
 *
 * #7ed348 is the brand and it is a light colour: white on it measures 1.86:1,
 * which is not a readability preference but text nobody can read in daylight.
 * For a long time the answer here was to keep the light green and put near-black
 * on it — legible, and wrong-looking, because every filled control in the app
 * read as a pale sticker rather than a button.
 *
 * So the ink is fixed at white and the GREEN moves instead. #46811f carries
 * white at 4.75:1, which clears AA for normal text at any weight, and it still
 * reads as the same hue beside the brand.
 *
 * ── Where each one is used ──────────────────────────────────────────────────
 *
 * BRAND is for surfaces with nothing to read on them — progress bars, the
 * active nav marker, chart marks, a status dot. BRAND_FILL is for anything with
 * a label on top: contained buttons, filled chips, an active toggle. The split
 * is the whole point; a single green cannot do both jobs.
 */
export const BRAND_FILL = '#46811f'
/** One step further down, for the hover and pressed states of the above. */
export const BRAND_FILL_HOVER = '#3f741c'

/**
 * The same split, for the other green the app owns.
 *
 * "Active", "done", "signed off" are all one teal, and it had the brand's
 * problem in a sharper form: white on #12b886 is 2.55:1, so every filled
 * success chip was carrying near-black. SUCCESS_FILL is the step where white
 * reads (4.99:1); SUCCESS_MARK keeps the bright teal for things with nothing
 * written on them — a dot, a bar, a board colour.
 *
 * Status colours that arrive from the database are NOT this: those are data and
 * stay measured (see theme/contrast.js). This is only the teal we chose.
 */
export const SUCCESS_FILL = '#0c7f5c'
export const SUCCESS_MARK = '#12b886'

/**
 * The page behind every card, in light mode. The one line to change to change
 * the background.
 *
 * Barely green rather than grey: the ink, the hover washes and the brand are
 * all warm green, and a neutral page under them reads cold. It is two steps off
 * white, which is as far as it goes before a white card stops reading as a card
 * and starts reading as a panel — the contrast between page and card is what
 * the whole layout rests on.
 */
export const BACKGROUND = "#f6f8f4"

/**
 * The motion scale.
 *
 * Three durations and two curves, and nothing may invent a fourth. The whole
 * point of a scale this small is that everything moving on screen at once
 * agrees about how fast "quick" is — the moment one panel eases over 450ms
 * while the row beside it snaps in 120ms, the interface stops feeling like one
 * object.
 *
 * `fast` is for a state a finger is already on: hover, press, a checkbox. `base`
 * is for something arriving or leaving — a row, a card, a page. `slow` is for
 * things that move a long way, like a drawer crossing the screen.
 *
 * Nothing overshoots and nothing springs. A bounce reads as playful, which is
 * the wrong note for a screen where the numbers are somebody's invoice.
 */
export const MOTION = {
  fast: 120,
  base: 200,
  slow: 300,
  ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
}

/** A `transition` string over the named properties. */
export function transition(properties, duration = MOTION.base, easing = MOTION.ease) {
  const list = Array.isArray(properties) ? properties : [properties]
  return list.map((property) => `${property} ${duration}ms ${easing}`).join(', ')
}

const shared = {
  shape: { borderRadius: 10 },
  transitions: {
    duration: {
      shortest: MOTION.fast,
      shorter: MOTION.fast,
      short: MOTION.base,
      standard: MOTION.base,
      complex: MOTION.slow,
      enteringScreen: MOTION.base,
      leavingScreen: MOTION.fast,
    },
    easing: {
      easeInOut: MOTION.ease,
      easeOut: MOTION.easeOut,
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.375rem', fontWeight: 650, letterSpacing: '-0.015em' },
    h3: { fontSize: '1.125rem', fontWeight: 600 },
    h4: { fontSize: '1rem', fontWeight: 600 },
    body2: { fontSize: '0.875rem' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
}

const components = (mode) => ({
  MuiCssBaseline: {
    styleOverrides: {
      /* Every scroll the app performs itself — a route change, "jump to the
         newest message" — eases instead of teleporting. A user-driven scroll is
         untouched; `scroll-behavior` only applies to programmatic ones. */
      html: { scrollBehavior: 'smooth' },

      /*
       * ── The page behind everything ─────────────────────────────────────────
       *
       * A flat fill is what makes an interface look like a wireframe that was
       * shipped. Three things fix it without ever competing with the content:
       *
       *   1. Two very wide radial washes, one brand green from the top left and
       *      one cool from the right, at 5% and 3.5%. Far too weak to read as
       *      colour — what they do is stop the field being uniform, so a white
       *      card on it has somewhere to sit.
       *   2. A 32px grid at about 2% opacity. Invisible as lines; visible as
       *      the sense that the page has a surface.
       *   3. `background-attachment: fixed`, so the washes belong to the window
       *      rather than to the document — a long table scrolling through a
       *      gradient looks like the gradient is scrolling, which is the one
       *      thing worse than no gradient.
       *
       * Everything is on `body` and under `background-color`, which stays the
       * palette value: nothing here changes what any text is read against, so
       * no contrast that was measured stops being true.
       */
      /*
       * ── The texture is a layer, not part of the colour ─────────────────────
       *
       * This used to be `background-image` on `body` itself, and that was a
       * trap. `background` is a shorthand: anybody who changes the page colour
       * the obvious way —
       *
       *     body { background: #eef1ea }
       *
       * — resets `background-image` to `none` in the same declaration and
       * deletes every layer below without touching a line that mentions them.
       * No error, no warning, and the only symptom is that the page looks flat
       * again.
       *
       * So the texture lives on a fixed pseudo-element behind the content
       * instead. The page colour is free to be set from anywhere — the palette,
       * a stylesheet, a browser extension — and the layer survives it, because
       * nothing about `body`'s own background can reach a child.
       *
       * To change the colour, change BACKGROUND above. That is the only place
       * it lives.
       */
      "body::before": {
        content: '""',
        position: "fixed",
        inset: 0,
        // Behind the content, in front of the page colour. Content is in normal
        // flow at `z-index: auto`, so it paints over this without anything
        // having to opt in.
        zIndex: -1,
        pointerEvents: "none",
        backgroundImage:
          mode === 'dark'
            ? [
                "radial-gradient(1300px 650px at 6% -12%, rgba(126,211,72,0.08), transparent 62%)",
                "radial-gradient(1000px 560px at 100% -4%, rgba(47,134,224,0.07), transparent 58%)",
                "linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px)",
                "linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)",
              ].join(", ")
            : [
                "radial-gradient(1300px 650px at 6% -12%, rgba(126,211,72,0.13), transparent 62%)",
                "radial-gradient(1000px 560px at 100% -4%, rgba(47,134,224,0.06), transparent 58%)",
                // 1.4%, down from 2.2%. Once the shell stopped painting over it
                // the grid was visible for the first time, and across the empty
                // half of a dashboard it read as graph paper rather than as a
                // surface. The wash does more of the work now and the grid less.
                "linear-gradient(rgba(16,35,10,0.014) 1px, transparent 1px)",
                "linear-gradient(90deg, rgba(16,35,10,0.014) 1px, transparent 1px)",
              ].join(", "),
        backgroundSize: "auto, auto, 32px 32px, 32px 32px",
        /*
         * All four layers are viewport-fixed now, the grid included.
         *
         * It used to scroll with the page, on the argument that a grid which
         * does not move reads as a pattern printed on the glass. At 2% opacity
         * that is not a difference anybody can see, and it cost a repaint of
         * the whole backdrop on every scroll — so the honest version is the
         * cheap one.
         */
      },
      /* One switch for all of it. Someone who has asked their operating system
         to stop moving things has asked once, for everything, and no component
         gets to decide it is the exception. */
      '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
          scrollBehavior: 'auto !important',
        },
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        transition: transition(['background-color', 'border-color', 'color', 'box-shadow'], MOTION.fast),
      },
      /*
       * White on a green dark enough to hold it.
       *
       * The brand itself cannot: white on #7ed348 is 1.86:1. Rather than put
       * near-black on a green button — which reads as a sticker rather than a
       * button — the fill drops to #46811f, where white measures 4.75:1.
       */
      containedPrimary: {
        backgroundColor: BRAND_FILL,
        color: '#ffffff',
        '&:hover': { backgroundColor: BRAND_FILL_HOVER },
        /* Stated explicitly, or MUI derives the disabled colours from the
           palette's main and the button turns pale green with white on it. */
        '&.Mui-disabled': {
          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(16,35,10,0.12)',
          color: mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(16,35,10,0.35)',
        },
      },
      // Brand green on white is about 1.8:1. A text button carries words and
      // nothing else, so it has to use the dark step.
      textPrimary: { color: '#4d8f22' },
      outlinedPrimary: { color: '#4d8f22' },
    },
    defaultProps: { disableElevation: true },
  },

  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(16,35,10,0.10)'}`,
        boxShadow: 'none',
        // Cards that are clickable move their border to brand green on hover.
        // Transitioning it here means no card has to remember to.
        transition: transition(['border-color', 'background-color'], MOTION.fast),
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: { fontWeight: 500, transition: transition(['background-color', 'border-color', 'color'], MOTION.fast) },
      /* A toggled filter is a green background with a word on it, so it follows
         the same rule as a button: white ink, and the green moves to carry it.
         The icon inherits, or a chip ends up with a white label and a dark
         glyph beside it. */
      filledPrimary: {
        backgroundColor: BRAND_FILL,
        color: '#ffffff',
        '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: 'inherit' },
        '&:hover, &.MuiChip-clickable:hover': { backgroundColor: BRAND_FILL_HOVER },
      },
    },
  },

  MuiTooltip: {
    defaultProps: { arrow: true, enterDelay: 400, enterNextDelay: 200 },
  },

  /*
   * Labels above the field, not inside its border.
   *
   * MUI's outlined default cuts a notch in the top border and parks the label in
   * it. With a value present the label sits on the border line and collides with
   * the text; worse, when a form fails validation every label in the group turns
   * red at once, so the one field actually at fault is not identifiable.
   *
   * Static labels fix both: the label is a caption above its box, the notch is
   * gone, and `error` reddens only the field that carries it.
   */
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
      slotProps: { inputLabel: { shrink: true } },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        position: 'static',
        transform: 'none',
        maxWidth: '100%',
        fontSize: '0.75rem',
        fontWeight: 500,
        marginBottom: 4,
        transition: transition('color', MOTION.fast),
        // Focus moves the label to the dark step, not the brand green: 12px of
        // #7ed348 on white is about 1.8:1 and effectively disappears.
        '&.Mui-focused': { color: '#4d8f22' },
        // Declared after, and deliberately: both selectors are equally specific,
        // so source order decides. A field that is both focused and at fault has
        // to read as at fault — a green label over a red box says the opposite of
        // the message underneath it.
        '&.Mui-error': { color: '#e04b3c' },
      },
    },
  },
  /*
   * The field itself, to the design system's numbers rather than MUI's.
   *
   * Four of them differ, and all four are visible side by side: the radius is
   * the BUTTON radius (8) and not the card radius (10); the border is the app's
   * divider, where MUI's default outline is a much darker rgba(0,0,0,0.23); the
   * box sits on the card surface rather than on nothing; and the text is 14px,
   * the app's reading size, where MUI's input default is 16.
   *
   * Together they are the difference between a form that looks like MUI with a
   * palette applied and one that looks like it was drawn with the cards it sits
   * between.
   */
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        fontSize: '0.875rem',
        transition: transition(['background-color', 'box-shadow'], MOTION.fast),

        // Hover is a 4% ink wash, never a colour change — the same rule the nav,
        // the table rows and the menu items follow.
        '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.divider,
        },
        '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
          backgroundColor: theme.palette.action.hover,
        },

        /*
         * The focus ring, outside the box.
         *
         * MUI's own focus state thickens the border to 2px of brand green —
         * #7ed348, which is about 1.8:1 on white and reads as a slightly
         * different border rather than as focus. The app's ring is 2px of brand
         * DARK at 2px offset, set globally in index.css for everything else; this
         * puts the same ring on the whole field rather than on the bare <input>
         * inside it, where it would be drawn within the border.
         */
        '&.Mui-focused': {
          outline: `2px solid ${theme.palette.primary.dark}`,
          outlineOffset: 2,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 1,
          borderColor: theme.palette.divider,
        },
        '&.Mui-error.Mui-focused': { outlineColor: theme.palette.error.main },

        // Disabled is dimmed whole, rather than having its text greyed and its
        // border left at full strength.
        '&.Mui-disabled': { opacity: 0.55, backgroundColor: 'transparent' },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
      }),
      input: {
        // 11px 12px at the default size, 8px 10px when small.
        padding: '11px 12px',
        '&.MuiInputBase-inputSizeSmall': { padding: '8px 10px' },
      },
      multiline: { padding: 0 },
      notchedOutline: ({ theme }) => ({
        top: 0,
        borderColor: theme.palette.divider,
        transition: transition('border-color', MOTION.fast),
        // The notch exists only to make room for a label that is no longer there.
        '& legend': { display: 'none' },
      }),
    },
  },

  // The chevron is text.secondary, like every other glyph that is not doing
  // anything on its own.
  MuiSelect: {
    styleOverrides: {
      icon: ({ theme }) => ({ color: theme.palette.text.secondary }),
    },
  },

  /*
   * A segmented control: one bordered group, dividers between, and the selected
   * segment carrying an 8% ink wash rather than a colour.
   *
   * MUI's default gives each button its own border and paints the selected one
   * with the primary colour, which on brand green means an unreadable pair and a
   * control that shouts louder than the page title beside it.
   */
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }),
      grouped: ({ theme }) => ({
        border: 'none',
        borderRadius: 0,
        '&:not(:first-of-type)': { borderLeft: `1px solid ${theme.palette.divider}`, marginLeft: 0 },
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: 34,
        height: 32,
        padding: '0 8px',
        color: theme.palette.text.secondary,
        transition: transition(['background-color', 'color'], MOTION.fast),
        '&:hover': { backgroundColor: theme.palette.action.hover },
        '&.Mui-selected': {
          backgroundColor: theme.palette.action.selected,
          color: theme.palette.text.primary,
          '&:hover': { backgroundColor: theme.palette.action.selected },
        },
      }),
    },
  },
  MuiFormHelperText: {
    // 14px of indent lines helper text up with the text inside a notched field.
    // The label is above the box now, so the helper belongs at the box's edge.
    styleOverrides: { root: { marginLeft: 2, marginRight: 0 } },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: { transition: transition(['background-color', 'color'], MOTION.fast) },
    },
  },
  MuiMenuItem: {
    styleOverrides: { root: { transition: transition('background-color', MOTION.fast) } },
  },
  MuiTableRow: {
    styleOverrides: { root: { transition: transition('background-color', MOTION.fast) } },
  },
  MuiIconButton: {
    styleOverrides: { root: { transition: transition(['background-color', 'color'], MOTION.fast) } },
  },
  MuiCheckbox: {
    styleOverrides: { root: { transition: transition('color', MOTION.fast) } },
  },
  /*
   * Initials in white, in both themes.
   *
   * MUI's default ink for an avatar is the page background, which means the
   * same two letters on the same coloured disc came out near-white in light
   * mode and near-black in dark — and next to a photo avatar, which has no ink
   * at all, the flip looked like a bug rather than a theme. The tints are
   * chosen so white clears AA on every one of them.
   */
  MuiAvatar: {
    styleOverrides: { root: { color: '#ffffff' } },
  },
  MuiLinearProgress: {
    styleOverrides: {
      bar: { transition: transition('transform', MOTION.slow, MOTION.easeOut) },
    },
  },
  MuiTab: {
    styleOverrides: { root: { transition: transition('color', MOTION.fast) } },
  },

  /*
   * ── One dialog, fifteen times ──────────────────────────────────────────────
   *
   * Every modal in the app was reaching for its own padding and its own title
   * size, so the log-time dialog and the invoice dialog were recognisably
   * different objects. They are the same object: a sheet that arrives over the
   * page and asks for one thing. Settling the shape here is what makes the
   * fifteenth dialog somebody writes look like the first fourteen without them
   * having to look at the first fourteen.
   *
   * The corner is rounder than the 10px base and the border is there in both
   * modes. A modal is the largest surface the app ever floats, and at that size
   * a shadow alone stops reading as an edge — against a pale background it goes
   * soft, and in dark mode it disappears entirely.
   *
   * Dialogs and drawers travel further than a hover, so they get the longer
   * step — and leave faster than they arrive, because a dismissal that lingers
   * feels like the app did not hear you.
   */
  MuiDialog: {
    defaultProps: { transitionDuration: { enter: MOTION.slow, exit: MOTION.fast } },
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: 14,
        border: `1px solid ${theme.palette.divider}`,
        backgroundImage: 'none',
        /*
         * Two shadows, not one.
         *
         * The tight one sits the sheet on the page; the wide, soft one is the
         * light falling off it. A single large blur reads as fog — the near
         * shadow is what makes the edge look like an edge.
         */
        boxShadow:
          mode === 'dark'
            ? '0 2px 6px rgba(0,0,0,0.5), 0 24px 56px -12px rgba(0,0,0,0.65)'
            : '0 2px 6px rgba(16,35,10,0.07), 0 24px 56px -12px rgba(16,35,10,0.22)',
        // Below this the sheet is the screen, and a rounded corner over a full
        // viewport just wastes the bottom row.
        [theme.breakpoints.down('sm')]: { borderRadius: 12, margin: 16, width: 'calc(100% - 32px)' },
      }),
    },
  },

  /*
   * The backdrop is ink, not black, for the same reason the hover wash is: the
   * page behind it is faintly green, and a neutral veil over it reads cold.
   * The blur is small on purpose — enough to push the page back, not enough to
   * turn the thing somebody was reading into a smear they cannot refer to.
   */
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: mode === 'dark' ? 'rgba(0,0,0,0.62)' : 'rgba(16,35,10,0.42)',
        backdropFilter: 'blur(3px)',
        // Invisible is invisible: the MUI class for an invisible backdrop must
        // stay that way, or every menu grows a veil.
        '&.MuiBackdrop-invisible': { backgroundColor: 'transparent', backdropFilter: 'none' },
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '20px 24px 14px',
        fontSize: '1.0625rem',
        fontWeight: 650,
        letterSpacing: '-0.01em',
        lineHeight: 1.35,
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: { padding: '20px 24px' },
      // `dividers` pulls the padding in to 16px by default, which puts the first
      // field closer to the rule above it than to the field below. Same 20 both
      // ways: the rule is the edge of the content, not part of it.
      dividers: { paddingTop: 20, paddingBottom: 20 },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      // The gap is the whole point. Cancel and a destructive confirm sitting 6px
      // apart is how somebody clicks the wrong one.
      root: { padding: '16px 24px', gap: 8, '& > :not(style) ~ :not(style)': { marginLeft: 0 } },
    },
  },
  MuiDrawer: {
    defaultProps: { transitionDuration: { enter: MOTION.slow, exit: MOTION.base } },
  },
  MuiMenu: {
    defaultProps: { transitionDuration: { enter: MOTION.base, exit: MOTION.fast } },
  },
  MuiPopover: {
    defaultProps: { transitionDuration: { enter: MOTION.base, exit: MOTION.fast } },
  },
})

export function buildTheme(mode = 'light') {
  const isDark = mode === 'dark'

  return createTheme({
    ...shared,
    palette: {
      mode,
      primary: {
        main: BRAND,
        // Dark enough to carry white text — used for links, active nav and
        // anything where the brand has to be legible as type.
        dark: '#4d8f22',
        light: '#a5e37f',
        contrastText: '#10230a',
      },
      secondary: { main: isDark ? '#8ab4f8' : '#2f6fd0' },
      /*
       * Not the brand green: see the note above.
       *
       * `main` is the fill and carries white. `dark` is the step used as TYPE —
       * the word "Strong", a saved tick, the live dot — and has to move with the
       * theme, because one colour cannot be legible on white paper and on a
       * near-black page at the same time.
       */
      success: {
        main: SUCCESS_FILL,
        dark: isDark ? '#2fd0a0' : '#0a6b4e',
        light: SUCCESS_MARK,
        contrastText: '#ffffff',
      },
      warning: { main: "#e9a13b" },
      error: { main: "#e04b3c" },
      info: { main: "#2f86e0" },
      /*
       * The page colour, in one place.
       *
       * The texture over it is a separate layer (see `body::before`) and
       * survives any change made here, so this can be edited freely — it is the
       * only line that needs editing.
       *
       * `paper` is every card, dialog and menu. In light mode it stays white:
       * the design depends on a card being a lighter surface than the page it
       * sits on, and a grey card on a grey page is a flat screen with a border
       * drawn on it.
       */
      background: isDark
        ? { default: "#0e1410", paper: "#161d17" }
        : { default: BACKGROUND, paper: "#ffffff" },
      text: isDark
        ? { primary: "#e8efe6", secondary: "#9db09a" }
        : { primary: "#10230a", secondary: "#5a6b55" },
      divider: isDark ? "rgba(255,255,255,0.09)" : "rgba(16,35,10,0.10)",
      /*
       * The washes are ink, not black.
       *
       * MUI's defaults are rgba(0,0,0,0.04) and rgba(0,0,0,0.08) — neutral grey
       * on a background that is faintly green. One row at a time it is invisible;
       * across a table, a sidebar and a menu it is the difference between an
       * interface tinted one way and one tinted two.
       */
      action: isDark
        ? {
            hover: 'rgba(255,255,255,0.06)',
            hoverOpacity: 0.06,
            selected: 'rgba(255,255,255,0.10)',
            selectedOpacity: 0.1,
          }
        : { hover: 'rgba(16,35,10,0.04)', hoverOpacity: 0.04, selected: 'rgba(16,35,10,0.08)', selectedOpacity: 0.08 },
    },
    components: components(mode),
  })
}

/** Status colours for the task board. Kept apart from the palette: these are data,
 *  and the backend sends its own `color` per status, which wins when present. */
export const STATUS_FALLBACK_COLORS = {
  backlog: '#8a8f98',
  todo: '#4650db',
  in_progress: '#2f86e0',
  blocked: '#e04b3c',
  in_review: '#e9a13b',
  done: '#12b886',
  cancelled: '#9e9e9e',
}

export const PRIORITY_COLORS = {
  low: '#8a8f98',
  medium: '#2f86e0',
  high: '#e9a13b',
  urgent: '#e04b3c',
}
